import React, { useEffect, useRef, useState } from "react";
import { Check, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

/**
 * Copy `text` to the clipboard. Never throws.
 * Some in-app browsers (Instagram, TikTok, older WebViews) block the async Clipboard API
 * outright, so a hidden-textarea + execCommand fallback runs whenever it fails.
 */
export async function copyToClipboard(text) {
  const value = String(text ?? "");
  if (!value) return false;

  try {
    if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch {
    /* fall through to the legacy path */
  }

  try {
    if (typeof document === "undefined") return false;
    const ta = document.createElement("textarea");
    ta.value = value;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.top = "0";
    ta.style.left = "0";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    ta.setSelectionRange(0, value.length);
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return Boolean(ok);
  } catch {
    return false;
  }
}

/**
 * CopyButton — copies `text` (a string, or a function returning one) and shows a
 * "Copied" state for two seconds. Always resolves to a toast, never to an exception.
 */
export default function CopyButton({
  text,
  label = "Copy",
  copiedLabel = "Copied",
  className,
  size,
  variant,
  successMessage = "Copied to your clipboard.",
  onCopied,
  ...rest
}) {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const handle = async () => {
    let value = "";
    try {
      value = typeof text === "function" ? text() : text;
    } catch {
      value = "";
    }

    const ok = await copyToClipboard(value);
    if (!ok) {
      toast.error("Couldn't copy — select the text and copy manually");
      return;
    }

    setCopied(true);
    toast.success(successMessage);
    try {
      onCopied?.(String(value ?? ""));
    } catch {
      /* a caller's analytics hook must never break the copy */
    }
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Button
      type="button"
      size={size}
      variant={variant}
      className={cn(className)}
      onClick={handle}
      aria-live="polite"
      {...rest}
    >
      {copied ? <Check className="mr-2 h-4 w-4" /> : <Copy className="mr-2 h-4 w-4" />}
      {copied ? copiedLabel : label}
    </Button>
  );
}
